import AddIcon from '@mui/icons-material/Add'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState, type ReactNode } from 'react'
import { describeError } from '../api/client'
import { ErrorBlock, LoadingBlock } from './Feedback'

export type FieldType = 'text' | 'multiline' | 'number' | 'select' | 'switch' | 'date' | 'password'

export interface CrudField {
  name: string
  label: string
  type?: FieldType
  options?: { value: string; label: string }[]
  required?: boolean
  helperText?: string
  /** Codes are identity in this system: set once, never edited. */
  lockedOnEdit?: boolean
  /** Half-width by default; set to true for a field that needs the whole row. */
  full?: boolean
  defaultValue?: unknown
}

export interface CrudColumn<T> {
  label: string
  render: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
}

export type FormValues = Record<string, unknown>

interface QueryLike {
  isLoading: boolean
  error: unknown
}

export interface CrudTableProps<T extends { id: string }> {
  query: QueryLike
  rows: T[]
  columns: CrudColumn<T>[]
  fields: CrudField[]
  /** Used instead of `fields` when editing, for forms that differ from the create form. */
  editFields?: CrudField[]
  /** Row -> form values when editing. Defaults to the row itself. */
  toFormValues?: (row: T) => FormValues
  /** Names the row in the delete confirmation, e.g. "cement 53 grade". */
  describeRow: (row: T) => string
  onCreate?: (values: FormValues) => Promise<unknown>
  onUpdate?: (row: T, values: FormValues) => Promise<unknown>
  onDelete?: (row: T) => Promise<unknown>
  addLabel?: string
  /** Says what deleting actually does, since most deletes here retire rather than erase. */
  deleteNote?: string
  emptyMessage?: string
  toolbar?: ReactNode
  /** Extra per-row actions rendered before edit and delete. */
  rowActions?: (row: T) => ReactNode
}

/**
 * One table, one dialog, for every master and transaction list in the app.
 *
 * <p>Built because the alternative is twenty near-identical edit dialogs that drift apart:
 * each screen supplies its columns and its field list, and add, edit and delete then look
 * and behave the same everywhere.</p>
 */
export function CrudTable<T extends { id: string }>({
  query,
  rows,
  columns,
  fields,
  editFields,
  toFormValues,
  describeRow,
  onCreate,
  onUpdate,
  onDelete,
  addLabel = 'Add',
  deleteNote,
  emptyMessage = 'Nothing here yet.',
  toolbar,
  rowActions,
}: CrudTableProps<T>) {
  const [editing, setEditing] = useState<{ row: T | null; values: FormValues } | null>(null)
  const [confirming, setConfirming] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const canEdit = Boolean(onUpdate)
  const canDelete = Boolean(onDelete)
  const showActions = canEdit || canDelete || Boolean(rowActions)

  function openCreate() {
    const values: FormValues = {}
    for (const field of fields) {
      values[field.name] = field.defaultValue ?? (field.type === 'switch' ? true : '')
    }
    setError(null)
    setEditing({ row: null, values })
  }

  function openEdit(row: T) {
    const source = toFormValues ? toFormValues(row) : (row as unknown as FormValues)
    const values: FormValues = {}
    for (const field of editFields ?? fields) {
      const value = source[field.name]
      values[field.name] = value === null || value === undefined
        ? (field.type === 'switch' ? false : '')
        : value
    }
    setError(null)
    setEditing({ row, values })
  }

  async function save() {
    if (!editing) return
    setBusy(true)
    setError(null)
    try {
      if (editing.row && onUpdate) {
        await onUpdate(editing.row, editing.values)
      } else if (onCreate) {
        await onCreate(editing.values)
      }
      setEditing(null)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirming || !onDelete) return
    setBusy(true)
    setError(null)
    try {
      await onDelete(confirming)
      setConfirming(null)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const activeFields = editing?.row ? (editFields ?? fields) : fields

  const requiredMissing = editing
    ? activeFields.some(
        (f) =>
          f.required &&
          !(editing.row && f.lockedOnEdit) &&
          String(editing.values[f.name] ?? '').trim() === '',
      )
    : false

  if (query.isLoading) return <LoadingBlock />
  if (query.error) {
    return (
      <Box sx={{ p: 2 }}>
        <ErrorBlock error={query.error} />
      </Box>
    )
  }

  return (
    <>
      {(toolbar || onCreate) && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            p: 2,
            pb: toolbar ? 2 : 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>{toolbar}</Box>
          {onCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {addLabel}
            </Button>
          )}
        </Box>
      )}

      {Boolean(error) && !editing && !confirming && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {describeError(error)}
          </Alert>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.label} align={c.align}>
                  {c.label}
                </TableCell>
              ))}
              {showActions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                {columns.map((c) => (
                  <TableCell key={c.label} align={c.align}>
                    {c.render(row)}
                  </TableCell>
                ))}
                {showActions && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {rowActions?.(row)}
                    {canEdit && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setError(null)
                            setConfirming(row)
                          }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (showActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 5, color: 'text.secondary' }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing?.row ? `Edit ${describeRow(editing.row)}` : addLabel}
        </DialogTitle>
        <DialogContent>
          {Boolean(error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {describeError(error)}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: 1,
            }}
          >
            {activeFields.map((field) => {
              const locked = Boolean(editing?.row && field.lockedOnEdit)
              const value = editing?.values[field.name]
              const set = (next: unknown) =>
                setEditing((prev) =>
                  prev ? { ...prev, values: { ...prev.values, [field.name]: next } } : prev,
                )

              const span = field.full || field.type === 'multiline' ? { gridColumn: '1 / -1' } : undefined

              if (field.type === 'switch') {
                return (
                  <Box key={field.name} sx={span}>
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(value)} onChange={(e) => set(e.target.checked)} />
                      }
                      label={field.label}
                    />
                    {field.helperText && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {field.helperText}
                      </Typography>
                    )}
                  </Box>
                )
              }

              return (
                <TextField
                  key={field.name}
                  sx={span}
                  label={field.label}
                  value={value ?? ''}
                  disabled={locked}
                  required={field.required}
                  helperText={locked ? 'Set once and not editable' : field.helperText}
                  select={field.type === 'select'}
                  type={
                    field.type === 'number'
                      ? 'number'
                      : field.type === 'date'
                        ? 'date'
                        : field.type === 'password'
                          ? 'password'
                          : 'text'
                  }
                  multiline={field.type === 'multiline'}
                  minRows={field.type === 'multiline' ? 2 : undefined}
                  slotProps={field.type === 'date' ? { inputLabel: { shrink: true } } : undefined}
                  onChange={(e) => set(e.target.value)}
                >
                  {field.type === 'select' && (
                    <MenuItem value="">
                      <em>Not set</em>
                    </MenuItem>
                  )}
                  {field.options?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void save()} disabled={busy || requiredMissing}>
            {editing?.row ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {confirming ? describeRow(confirming) : ''}?</DialogTitle>
        <DialogContent>
          {Boolean(error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {describeError(error)}
            </Alert>
          )}
          <DialogContentText>
            {deleteNote ??
              'This removes it from the pickers. Anything already recorded against it keeps its name.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirming(null)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void remove()} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/** Small helper so a screen can lay out its own toolbar filters consistently. */
export function CrudToolbar({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>{children}</Box>
  )
}
